import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/Common";

const ETIQUETAS = {
  superadmin: "SuperAdmin", admin: "Administración", coordinadora: "Coordinadora",
  secretaria: "Secretaria", docente: "Docente", tutor: "Padre / Tutor",
};

const variante = (rol) => (["superadmin", "admin"].includes(rol) ? "default"
  : rol === "docente" ? "secondary" : "outline");

const vinculos = (u) => {
  if (u.rol === "tutor") {
    return u.estudiantes?.length ? u.estudiantes.map((e) => e.nombre).join(", ") : "sin hijos";
  }
  if (u.rol === "docente") {
    return u.materias?.length ? `${u.materias.length} materia(s)` : "sin materias";
  }
  return "—";
};

const estado = (u) => (u.activo === false ? "desactivada"
  : u.sin_acceso ? "ficha sin acceso" : "activa");

const Fila = ({ u, acciones }) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-4 py-3 last:border-0 hover:bg-muted/30">
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium">{u.nombre}</span>
      <span className="block truncate font-mono text-xs text-muted-foreground">
        {u.email} · {u.telefono || "sin teléfono"} · {vinculos(u)}
      </span>
    </span>
    <Badge variant={variante(u.rol)}>{ETIQUETAS[u.rol] || u.rol}</Badge>
    {u.activo === false && <Badge variant="destructive">desactivada</Badge>}
    <div className="flex flex-wrap gap-2">{acciones(u)}</div>
  </div>
);

const Tabla = ({ usuarios, acciones }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[980px] text-sm">
      <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
        <tr>
          <th className="px-4 py-3">Nombre</th>
          <th className="px-4 py-3">Correo</th>
          <th className="px-4 py-3">Rol</th>
          <th className="px-4 py-3">Teléfono</th>
          <th className="px-4 py-3">Cédula / RNC</th>
          <th className="px-4 py-3">Hijos / materias</th>
          <th className="px-4 py-3">Estado</th>
          <th className="px-4 py-3"></th>
        </tr>
      </thead>
      <tbody>
        {usuarios.map((u) => (
          <tr key={u.id} className="border-t border-border/60 hover:bg-muted/30">
            <td className="px-4 py-3 font-medium">{u.nombre}</td>
            <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
            <td className="px-4 py-3">
              <Badge variant={variante(u.rol)}>{ETIQUETAS[u.rol] || u.rol}</Badge>
            </td>
            <td className="px-4 py-3 text-muted-foreground">{u.telefono || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs">{u.cedula_rnc || "—"}</td>
            <td className="px-4 py-3 text-muted-foreground">{vinculos(u)}</td>
            <td className="px-4 py-3">
              <span className={u.activo === false ? "text-destructive" : "text-muted-foreground"}>
                {estado(u)}
              </span>
            </td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap justify-end gap-2">{acciones(u)}</div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Bloque = ({ vista, usuarios, tarjeta, acciones }) => {
  if (vista === "tarjetas") {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {usuarios.map((u) => tarjeta(u))}
      </div>
    );
  }
  if (vista === "lista") {
    return (
      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        {usuarios.map((u) => <Fila key={u.id} u={u} acciones={acciones} />)}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
      <Tabla usuarios={usuarios} acciones={acciones} />
    </div>
  );
};

const ORDEN = ["superadmin", "admin", "coordinadora", "secretaria", "docente", "tutor"];

/** Cuentas en tarjetas, lista compacta o tabla, con agrupación por rol. */
export const VistaUsuarios = ({ usuarios, vista, agrupado, tarjeta, acciones }) => {
  if (usuarios.length === 0) {
    return <Empty testId="usuarios-vacios">Ningún usuario coincide con la búsqueda.</Empty>;
  }
  if (!agrupado) {
    return (
      <div data-testid="lista-usuarios">
        <Bloque vista={vista} usuarios={usuarios} tarjeta={tarjeta} acciones={acciones} />
      </div>
    );
  }
  const grupos = ORDEN
    .map((rol) => [rol, usuarios.filter((u) => u.rol === rol)])
    .filter(([, lista]) => lista.length > 0);
  return (
    <div className="space-y-8" data-testid="lista-usuarios">
      {grupos.map(([rol, lista]) => (
        <section key={rol} data-testid={`grupo-rol-${rol}`}>
          <div className="mb-3 flex items-center gap-3">
            <h3 className="font-display text-base font-semibold">{ETIQUETAS[rol] || rol}</h3>
            <Badge variant="outline" data-testid={`contador-rol-${rol}`}>{lista.length}</Badge>
          </div>
          <Bloque vista={vista} usuarios={lista} tarjeta={tarjeta} acciones={acciones} />
        </section>
      ))}
    </div>
  );
};
